// =============================================================================
// App — top-level router
// =============================================================================
// Routes:
//   /login              — CCF Front Office sign-in
//   /reset-password     — Supabase recovery link landing page
//   /                   — Front Office dashboard (active team member required)
//   /huddler            — internal team tool (active team member required)
//   /recruits           — recruit profile list
//   /recruits/:id       — tabbed recruit profile editor
//   /plans              — payment-plan progress monitor (staff, read-only)
//   /call-sheet         — service-delivery to-do board (SQ-S1; call_sheet.view)
//   /checkout/:token    — public recruit-facing checkout page
//   /checkout/:token/success — post-payment success page
// =============================================================================

import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabase.js';
import { useMe } from './lib/flux/useMe.js';

import Login from './pages/Login.jsx';
import ResetPassword from './pages/ResetPassword.jsx';
import Dashboard from './pages/Dashboard.jsx';
import Huddler from './pages/Huddler.jsx';
import RecruitList from './pages/RecruitList.jsx';
import RecruitEdit from './pages/RecruitEdit.jsx';
import Notifications from './pages/Notifications.jsx';
import PlanProgress from './pages/PlanProgress.jsx';
import PlanBuilder from './pages/PlanBuilder.jsx';
import TeamManager from './pages/TeamManager.jsx';
import TeamMemberProfile from './pages/TeamMemberProfile.jsx';
import ParentPlanReview from './pages/ParentPlanReview.jsx';
// SQ-S1 — The Call Sheet. Same guard, same style; the page hides behind
// can('call_sheet.view') and the server refuses regardless.
import CallSheet from './pages/CallSheet.jsx';
// P9a W-7(c) — FluxEngine operator client. `/campaigns`, not `/flux/…`: the
// path matches `/recruits` and `/plans`, and carries no product codename.
import CampaignList from './pages/campaigns/CampaignList.jsx';
import CampaignBuilder from './pages/campaigns/CampaignBuilder.jsx';
import CampaignDetail from './pages/campaigns/CampaignDetail.jsx';
// P9b W-12 - contacts, import and the RC portal. Same guard, same style.
import CampaignContacts from './pages/campaigns/CampaignContacts.jsx';
import CampaignContactImport from './pages/campaigns/CampaignContactImport.jsx';
import CampaignPortal from './pages/campaigns/CampaignPortal.jsx';
// S100 Build 2 — the player's own page. Recruit-direct voice; no Front Office
// chrome. Guarded by RequirePlayer, RequireTeamMember's mirror.
// S103 — the kid's read-only campaign report. Player side of the split: same
// RequirePlayer guard as /my-profile, so a staff session is redirected to the
// Front Office exactly as it is there.
import PlayerPortal from './components/PlayerPortal.jsx';
import Checkout from './pages/Checkout.jsx';
import CheckoutSuccess from './pages/CheckoutSuccess.jsx';
import SkipChatV2 from './components/SkipChatV2.jsx';

function RequireTeamMember({ children }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    async function check() {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        setLoading(false);
        return;
      }

      const userId = sessionData.session.user.id;
      const { data: teamRow } = await supabase
        .from('team')
        .select('status, role')
        .eq('auth_user_id', userId)
        .single();

      const ok = teamRow && teamRow.status === 'active' && teamRow.role !== 'system';
      if (!ok) {
        // S100 Build 2 — CONTRACT CHANGE, declared: a signed-in PLAYER hitting
        // a staff route is REDIRECTED, not signed out. The old unconditional
        // signOut would eat the invite flow — the kid sets a password, lands
        // on '/', and is logged straight back out. A session that is neither
        // staff nor player is signed out exactly as before.
        const { data: playerRow } = await supabase
          .from('player_profiles')
          .select('recruit_id')
          .eq('user_id', userId)
          .maybeSingle();
        if (playerRow) {
          navigate('/my-profile', { replace: true });
          return;
        }
        await supabase.auth.signOut();
        navigate('/login', { replace: true });
        return;
      }

      setAllowed(true);
      setLoading(false);
    }

    check();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        setAllowed(false);
        navigate('/login', { replace: true });
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-silver-mute">Loading…</div>;
  }
  if (!allowed) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

// S100 Build 2 — the player-side guard, RequireTeamMember's mirror. A session
// with a player_profiles row gets in; a staff session is sent to the Front
// Office; a nobody is signed out. No chrome decisions here — MyProfile owns
// its own masthead.
function RequirePlayer({ children }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    async function check() {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        navigate('/login', { replace: true });
        return;
      }
      const userId = sessionData.session.user.id;
      const { data: playerRow } = await supabase
        .from('player_profiles')
        .select('recruit_id')
        .eq('user_id', userId)
        .maybeSingle();
      if (!playerRow) {
        const { data: teamRow } = await supabase
          .from('team')
          .select('status')
          .eq('auth_user_id', userId)
          .maybeSingle();
        if (teamRow && teamRow.status === 'active') {
          navigate('/', { replace: true });          // staff poking a kid URL → Front Office
          return;
        }
        await supabase.auth.signOut();
        navigate('/login', { replace: true });
        return;
      }
      setAllowed(true);
      setLoading(false);
    }
    check();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) navigate('/login', { replace: true });
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-silver-mute">Loading…</div>;
  }
  if (!allowed) return <Navigate to="/login" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { me, loading } = useMe();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-silver-mute">Loading…</div>;
  if (me?.team?.role !== 'admin') return <div className="p-8">Access denied — Admin required.</div>;
  return children;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/plan-review/:token" element={<ParentPlanReview />} />
      <Route path="/" element={<RequireTeamMember><Dashboard /></RequireTeamMember>} />
      <Route path="/huddler" element={<RequireTeamMember><Huddler /></RequireTeamMember>} />
      <Route path="/invoice" element={<RequireTeamMember><Huddler invoiceMode /></RequireTeamMember>} />
      <Route path="/recruits" element={<RequireTeamMember><RecruitList /></RequireTeamMember>} />
      <Route path="/recruits/:id" element={<RequireTeamMember><RecruitEdit /></RequireTeamMember>} />
      <Route path="/notifications" element={<RequireTeamMember><Notifications /></RequireTeamMember>} />
      <Route path="/plans" element={<RequireTeamMember><PlanProgress /></RequireTeamMember>} />
      <Route path="/plan-builder" element={<RequireTeamMember><PlanBuilder /></RequireTeamMember>} />
      <Route path="/call-sheet" element={<RequireTeamMember><CallSheet /></RequireTeamMember>} />
      {/* S83 — Front Office team manager. Same guard as every other page;
          the page itself also refuses without team.manage, and the server
          refuses regardless. Three layers, none of them the client's to
          enforce. */}
      <Route path="/team" element={<RequireTeamMember><TeamManager /></RequireTeamMember>} />
      <Route path="/team-member-profile" element={<RequireTeamMember><TeamMemberProfile /></RequireTeamMember>} />
      {/* P9a W-7(c). Same guard, same style. /:id/edit is a path param, as
          /recruits/:id — RM used /campaigns/edit?id=. P9b adds /contacts,
          /contacts/import and /campaigns/mine beside these. */}
      <Route path="/campaigns" element={<RequireTeamMember><CampaignList /></RequireTeamMember>} />
      <Route path="/campaigns/new" element={<RequireTeamMember><CampaignBuilder /></RequireTeamMember>} />
      {/* P9b W-12. /campaigns/mine is a STATIC segment and React Router ranks
          it above /campaigns/:id, so an RC landing here is never routed into
          the detail page with id="mine". Kept adjacent so that stays visible. */}
      <Route path="/campaigns/mine" element={<RequireTeamMember><CampaignPortal /></RequireTeamMember>} />
      <Route path="/campaigns/:id" element={<RequireTeamMember><CampaignDetail /></RequireTeamMember>} />
      <Route path="/campaigns/:id/edit" element={<RequireTeamMember><CampaignBuilder /></RequireTeamMember>} />
      <Route path="/contacts" element={<RequireTeamMember><RequireAdmin><CampaignContacts /></RequireAdmin></RequireTeamMember>} />
      <Route path="/contacts/import" element={<RequireTeamMember><RequireAdmin><CampaignContactImport /></RequireAdmin></RequireTeamMember>} />
      <Route path="/my-profile" element={<RequirePlayer><PlayerPortal /></RequirePlayer>} />
      {/* Old S103 detail URLs are compatibility redirects only. The campaign
          report now lives in the Email Campaigns tab of the one player portal. */}
      <Route path="/my-outreach" element={<Navigate to="/my-profile?tab=email-campaigns" replace />} />
      <Route path="/my-outreach/:campaignId" element={<Navigate to="/my-profile?tab=email-campaigns" replace />} />
      <Route path="/checkout/:token" element={<Checkout />} />
      <Route path="/checkout/:token/success" element={<CheckoutSuccess />} />
      <Route path="/skip-embed" element={<div className="w-screen h-screen"><SkipChatV2 embedded /></div>} />
      <Route path="*" element={<div className="p-8">404 — Not found</div>} />
    </Routes>
  );
}
